// Data for the विभक्ति-रूप lab: every kit noun with its search text, the case-use notes of docs/03 §5.3,
// the चतुर्थी = षष्ठी twin (नि.४६), cell keys ('prathama.eka') and the letters needed to type this noun's forms.

import type {
    Case,
    CaseTable,
    Gender,
    Num,
    StemClass
} from '@/prakrit/lib/grammar/types'

import { kitNouns } from '@/prakrit/lib/grammar/build'
import { CASES, GENDERS, NUMBERS } from '@/prakrit/lib/grammar/types'
import { GENDER_LABEL, STEM_CLASS_LABEL } from '@/prakrit/lib/grammar/labels'
import { latinKey, searchText } from '@/prakrit/components/grammar/labs/lab-search'

export interface LabNoun {
    stem: string
    gender: Gender
    stemClass: StemClass
    hi: string
    en: string
    rules: string
    forms: CaseTable
    search: string
}

let cache: LabNoun[] | null = null

/** All kit nouns, in data/nouns.json order. */
export const labNouns = (): LabNoun[] => {
    if (!cache) {
        cache = kitNouns().map(n => ({
            stem: n.stem,
            gender: n.gender,
            stemClass: n.stem_class,
            hi: n.hi,
            en: n.en,
            rules: n.rules,
            forms: n.forms,
            search: searchText(n.stem, n.hi, n.en, latinKey(n.stem), GENDER_LABEL[n.gender].hi, STEM_CLASS_LABEL[n.stem_class].hi)
        }))
    }

    return cache
}

export const findNoun = (stem: string): LabNoun | undefined => {
    const key = stem.normalize('NFC')

    return labNouns().find(n => n.stem === key)
}

export const GENDER_ORDER: Gender[] = [...GENDERS]

const CLASS_ORDER: StemClass[] = ['a', 'ā', 'i', 'ī', 'u', 'ū', 'e', 'o']

/** The ending classes found among these nouns, in the order अ, आ, इ, ई, उ, ऊ. */
export const stemClassesOf = (nouns: LabNoun[]): StemClass[] => {
    const present = new Set(nouns.map(n => n.stemClass))

    return CLASS_ORDER.filter(c => present.has(c))
}

// ---------------------------------------------------------------- cells

export type CellKey = `${Case}.${Num}`

export const cellKey = (c: Case, n: Num): CellKey => `${c}.${n}`

export const parseCellKey = (key: string): { case: Case, number: Num } | null => {
    const [c, n] = key.split('.')

    if (!(CASES as readonly string[]).includes(c) || !(NUMBERS as readonly string[]).includes(n)) return null

    return { case: c as Case, number: n as Num }
}

/** Kids start with the three cases of the first sentences: who does it, to what, with what. */
export const KIDS_CASES: Case[] = ['prathama', 'dvitiya', 'tritiya']

/** चतुर्थी and षष्ठी have the same forms in Prakrit (नि.४६). */
export const CASE_TWIN: Partial<Record<Case, Case>> = { chaturthi: 'shashthi', shashthi: 'chaturthi' }

/** How each case is used (docs/03-GRAMMAR-RULES.md §5.3). */
export const CASE_USE: Record<Case, { hi: string, en: string }> = {
    prathama: {
        hi: 'कर्ता: जो काम करता है। वाक्य का मुख्य शब्द।',
        en: 'The doer: the one who acts, the subject of the sentence.'
    },
    dvitiya: {
        hi: 'कर्म: जिस पर काम का असर पड़ता है।',
        en: 'The object: what the action is done to.'
    },
    tritiya: {
        hi: 'करण (के द्वारा) और साथ। सह (साथ) और विणा (बिना) के साथ भी तृतीया आती है।',
        en: 'By means of, or together with. सह (with) and विणा (without) also take तृतीया.'
    },
    chaturthi: {
        hi: 'सम्प्रदान: किसके लिए। इसके रूप षष्ठी जैसे ही होते हैं।',
        en: 'For whom, to whom. Its forms are the same as the षष्ठी forms.'
    },
    panchami: {
        hi: 'अपादान: किससे अलग (से)। डरना (बीहइ) और घृणा करना (दुगुञ्छइ) के साथ भी पंचमी।',
        en: 'Away from. Also used with बीहइ (fears) and दुगुञ्छइ (dislikes).'
    },
    shashthi: {
        hi: 'सम्बन्ध: किसका, किसकी, किसके।',
        en: 'Belonging: of, ’s.'
    },
    saptami: {
        hi: 'अधिकरण: किसमें, किस पर।',
        en: 'Place or time: in, on, at.'
    },
    sambodhan: {
        hi: 'सम्बोधन: किसी को पुकारना (हे …)।',
        en: 'Calling someone: O …!'
    }
}

// ---------------------------------------------------------------- the on-screen letter helper

const allForms = (noun: LabNoun): string[] => {
    const out: string[] = []

    for (const c of CASES) {
        for (const n of NUMBERS) {
            for (const f of noun.forms[c]?.[n] ?? []) {
                if (f.status !== 'extrapolated') out.push(f.form.normalize('NFC'))
            }
        }
    }

    return out
}

const commonPrefix = (words: string[]): string => {
    if (words.length === 0) return ''

    let prefix = words[0]

    for (const w of words.slice(1)) {
        let i = 0

        while (i < prefix.length && i < w.length && prefix[i] === w[i]) i += 1

        prefix = prefix.slice(0, i)
    }

    // Never end the prefix on a virama or before a dependent sign that belongs to the next letter.
    while (prefix && /[्]$/.test(prefix)) prefix = prefix.slice(0, -1)

    return prefix
}

const INDEPENDENT_VOWEL = /[ऄ-औ]/
const DEPENDENT_SIGN = /[ा-ौ]/
const CONSONANT = /[क-ह]/

const rank = (ch: string): number => {
    if (INDEPENDENT_VOWEL.test(ch)) return 0
    if (DEPENDENT_SIGN.test(ch)) return 1
    if (ch === 'ं' || ch === 'ँ') return 2
    if (CONSONANT.test(ch)) return 3
    if (ch === '्') return 4

    return 5
}

export interface HelperKey {
    /** What the key inserts. */
    value: string
    /** What the key shows (a dependent sign is drawn on ◌). */
    label: string
    /** Spoken name for screen readers. */
    name: string
}

const SIGN_NAME: Record<string, string> = {
    'ा': 'आ की मात्रा',
    'ि': 'इ की मात्रा',
    'ी': 'ई की मात्रा',
    'ु': 'उ की मात्रा',
    'ू': 'ऊ की मात्रा',
    'े': 'ए की मात्रा',
    'ो': 'ओ की मात्रा',
    'ं': 'अनुस्वार',
    'ँ': 'चन्द्रबिन्दु',
    '्': 'हलन्त'
}

/**
 * The keys a learner needs to type every form of this noun without a Devanagari keyboard:
 * the part all forms share (पुरिस), then every letter and sign that follows it in some form.
 */
export const helperKeys = (noun: LabNoun): { stem: string, keys: HelperKey[] } => {
    const forms = allForms(noun)
    const stem = commonPrefix(forms)
    const chars = new Set<string>()

    for (const f of forms) {
        for (const ch of Array.from(f.slice(stem.length))) chars.add(ch)
    }

    const keys = Array.from(chars)
        .filter(ch => /[ऀ-ॿ]/.test(ch))
        .sort((a, b) => rank(a) - rank(b) || a.charCodeAt(0) - b.charCodeAt(0))
        .map(ch => ({
            value: ch,
            label: rank(ch) === 1 || rank(ch) === 2 || rank(ch) === 4 ? `◌${ch}` : ch,
            name: SIGN_NAME[ch] ?? ch
        }))

    return { stem, keys }
}
